import React, { useState, useEffect, useRef } from "react";
import {
  BatchInferenceDto,
  BatchInferenceResult,
  DeploymentInferenceDto,
  DeploymentResponseDto,
  ModelResponseDto,
  PredictionRunItem,
  ProjectResponseDto,
} from "../common/ObjectTypes";
import {
  getProjectByGuid,
  getProjectHasActivePredictionRun,
  getProjectHasActiveImportOrUpload,
  getProjectPredictionRuns,
  getProjects,
} from "../../api/projectsApi";
import { useProfile } from "../common/ProfileProvider";
import AiModelZooDetails from "./AiModelZooDetails";
import {
  batchDetectDeployments,
  batchClassifyDeployments,
  detectDeployment,
  getDeployments,
  classifyDeployment,
  getDeploymentPredictionRuns,
  getDeploymentHasActivePredictionRun,
  getDeploymentHasActiveImportOrUpload,
  getDeploymentByGuid,
  retryPredictionRun,
} from "../../api/deploymentsApi";
import { getModels } from "../../api/modelApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const AIMODELZOO_STORAGE_KEYS = {
  PROJECT: "lastSelectedInferenceProject",
  DEPLOYMENT: "lastSelectedInferenceDeployment",
} as const;

const ManageAiModelZooPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<ProjectResponseDto[]>([]);
  const [deployments, setDeployments] = useState<DeploymentResponseDto[]>([]);
  const [detectionModels, setDetectionModels] = useState<ModelResponseDto[]>(
    [],
  );
  const [classifierModels, setClassifierModels] = useState<ModelResponseDto[]>(
    [],
  );
  const [loadingProjects, setLoadingProjects] = useState(true);
  const { profile, profileLoading } = useProfile();
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [deploymentsLoading, setDeploymentsLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  // AKL-766: one model/threshold pair each for Detection and Classification,
  // shared regardless of scope (a single deployment or "All deployments") —
  // the unified redesign no longer has separate per-scope model state.
  const [selectedDetectionModel, setSelectedDetectionModel] =
    useState<ModelResponseDto | null>(null);
  const [selectedClassificationModel, setSelectedClassificationModel] =
    useState<ModelResponseDto | null>(null);
  const [selectedProject, setSelectedProject] =
    useState<ProjectResponseDto | null>(null);
  const [selectedDeployment, setSelectedDeployment] =
    useState<DeploymentResponseDto | null>(null);
  const [filesInAkili, setFilesInAkili] = useState(0);
  const [loadingFilesInAkili, setLoadingFilesInAkili] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [detecting, setDetecting] = useState(false);
  // Deployment prediction runs state
  const [deploymentPredictionRuns, setDeploymentPredictionRuns] = useState<
    PredictionRunItem[]
  >([]);
  const [loadingDeploymentPredictionRuns, setLoadingDeploymentPredictionRuns] =
    useState(false);
  const [deploymentRunPage, setDeploymentRunPage] = useState(1);
  // AKL-766: defaults to the largest page size so the new client-side run
  // history search (no backend search support — see PredictionRunHistory)
  // covers realistically all of a deployment's history without a second page.
  const [deploymentRunPageSize, setDeploymentRunPageSize] = useState(50);
  const [deploymentRunTotal, setDeploymentRunTotal] = useState(0);
  const [deploymentRunSortBy, setDeploymentRunSortBy] = useState("created_at");
  const [deploymentRunSortDirection, setDeploymentRunSortDirection] = useState<
    "asc" | "desc"
  >("desc");
  // Project prediction runs state
  const [projectPredictionRuns, setProjectPredictionRuns] = useState<
    PredictionRunItem[]
  >([]);
  const [loadingProjectPredictionRuns, setLoadingProjectPredictionRuns] =
    useState(false);
  const [projectRunPage, setProjectRunPage] = useState(1);
  // AKL-766: see deploymentRunPageSize — same rationale for the project scope.
  const [projectRunPageSize, setProjectRunPageSize] = useState(50);
  const [projectRunTotal, setProjectRunTotal] = useState(0);

  const [projectRunSortBy, setProjectRunSortBy] = useState("created_at");
  const [projectRunSortDirection, setProjectRunSortDirection] = useState<
    "asc" | "desc"
  >("desc");
  const [projectHasActive, setProjectHasActive] = useState(false);
  const [deploymentHasActive, setDeploymentHasActive] = useState(false);
  const { project_guid, deployment_guid } = useParams();

  const [importOrUploadInProgress, setImportOrUploadInProgress] =
    useState(false);
  const [projectImportOrUploadInProgress, setProjectImportOrUploadInProgress] =
    useState(false);
  const [inferenceInProgress, setInferenceInProgress] = useState(false);

  const [retryingRunIds, setRetryingRunIds] = useState<Set<number>>(new Set());
  // AKL-766: which confirmation dialog (if any) is open — "detection",
  // "classification", or "pipeline" ("Run full pipeline").
  const [confirmAction, setConfirmAction] = useState<
    "detection" | "classification" | "pipeline" | null
  >(null);

  const batchProjectIdRef = useRef<number | null>(null);
  const selectedDeploymentRef = useRef<DeploymentResponseDto | null>(null);
  const selectedProjectRef = useRef<ProjectResponseDto | null>(null);

  // AKL-766: no deployment selected (and the project has at least loaded its
  // deployment list) means the "All deployments" scope — the redesign's
  // unified Deployment selector's default/explicit choice, matching the
  // existing project-level batch behavior this replaces.
  const isAllDeployments =
    selectedProject !== null && selectedDeployment === null;

  useEffect(() => {
    const fetchProjects = async () => {
      setResultsLoaded(false);
      try {
        // UPLOAD_IMAGES is the permission every run-AI endpoint checks (Owner, Editor,
        // Contributor). AKL-1060: this used to send START_INFERENCE, which the API never
        // had, so the list came back unfiltered.
        const result = await getProjects({
          skip: 0,
          all_projects: true,
          permission: "UPLOAD_IMAGES",
        });
        setProjects(result.projects || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
      } finally {
        setResultsLoaded(true);
        setLoadingProjects(false);
      }
    };

    if (!profileLoading && profile) {
      fetchProjects();
      fetchModels();
    }
  }, [profileLoading, profile]);

  useEffect(() => {
    const lastSelectedProject = localStorage.getItem(
      AIMODELZOO_STORAGE_KEYS.PROJECT,
    );
    const project = lastSelectedProject
      ? JSON.parse(lastSelectedProject)
      : null;
    const lastSelectedDeployment = localStorage.getItem(
      AIMODELZOO_STORAGE_KEYS.DEPLOYMENT,
    );
    const deployment = lastSelectedDeployment
      ? JSON.parse(lastSelectedDeployment)
      : null;
    if (project && !project_guid) {
      if (deployment) {
        navigate(`/ai-processing/${project.guid}/${deployment.guid}`, {
          replace: true,
        });
      } else {
        navigate(`/ai-processing/${project.guid}`, { replace: true });
      }
    }
  }, [project_guid, deployment_guid]);

  useEffect(() => {
    const loadDataFromUrl = async () => {
      if (project_guid && !selectedProject) {
        const lastSelectedProject = localStorage.getItem(
          AIMODELZOO_STORAGE_KEYS.PROJECT,
        );
        let project = lastSelectedProject
          ? JSON.parse(lastSelectedProject)
          : null;
        if (project === null || project?.guid !== project_guid) {
          // `getProjectByGuid` rethrows since AKL-939; any failure (a 403 included) still
          // takes the "Project not found." path below, exactly as the swallowed `undefined`
          // did before.
          try {
            project = await getProjectByGuid(project_guid);
          } catch {
            project = undefined;
          }
        }
        if (project) {
          await handleSelectProject(project, false, false);
        } else {
          toast.error(t("Project not found."));
          navigate("/ai-processing", { replace: true });
          return;
        }
      }
      if (selectedProject && deployment_guid && !selectedDeployment) {
        const lastSelectedDeployment = localStorage.getItem(
          AIMODELZOO_STORAGE_KEYS.DEPLOYMENT,
        );
        let deployment = lastSelectedDeployment
          ? JSON.parse(lastSelectedDeployment)
          : null;
        if (deployment === null || deployment?.guid !== deployment_guid) {
          deployment = await getDeploymentByGuid(deployment_guid);
        }
        if (deployment) {
          if (deployment.project_id !== selectedProject.project_id) {
            toast.error(
              t(
                "Invalid URL: Deployment does not belong to the selected project.",
              ),
            );
            navigate(`/ai-processing/${selectedProject.guid}`, {
              replace: true,
            });
            return;
          }
          await handleSelectDeployment(deployment, false);
        } else {
          toast.error(t("Deployment not found."));
          navigate(`/ai-processing/${selectedProject.guid}`, { replace: true });
          return;
        }
      }
    };
    loadDataFromUrl();
  }, [deployment_guid, project_guid, selectedProject, selectedDeployment]);

  const fetchDeployments = async (project_id: number) => {
    setDeploymentsLoading(true);
    try {
      const temp = await getDeployments({ project_id, all_deployments: true });
      setDeployments(temp.deployments || []);
    } catch (err) {
      console.error("Error fetching deployments:", err);
      toast.error(t("Error loading deployments for this project."));
      setDeployments([]);
    } finally {
      setDeploymentsLoading(false);
    }
  };

  const fetchFilesInAkili = async (deployment: DeploymentResponseDto) => {
    if (deployment) {
      setLoadingFilesInAkili(true);
      try {
        const refreshedDeployment = await getDeploymentByGuid(deployment.guid);
        if (
          refreshedDeployment &&
          refreshedDeployment.guid === deployment.guid
        ) {
          setFilesInAkili(refreshedDeployment.media_asset_count);
        }
      } catch (error) {
        console.error("Error fetching files in Akili:", error);
        toast.error(t("Error loading file count for deployment."));
        setFilesInAkili(0);
      } finally {
        setLoadingFilesInAkili(false);
      }
    }
  };

  const fetchModels = async () => {
    setModelsLoading(true);
    try {
      const result = await getModels({ skip: 0, take: 100 });
      if (result && Array.isArray(result.models)) {
        setDetectionModels(
          result.models.filter((m) => m.model_type === "detector"),
        );
        setClassifierModels(
          result.models.filter((m) => m.model_type === "classifier"),
        );
      } else {
        setDetectionModels([]);
        setClassifierModels([]);
      }
    } catch (err: any) {
      setDetectionModels([]);
      setClassifierModels([]);
    } finally {
      setModelsLoading(false);
    }
  };

  const fetchDeploymentPredictionRuns = async (
    deployment: DeploymentResponseDto,
    page: number,
    pageSize: number,
    sortBy = "created_at",
    sortDirection: "asc" | "desc" = "desc",
  ) => {
    if (deployment?.deployment_id) {
      const deploymentIdAtStart = deployment.deployment_id;
      setLoadingDeploymentPredictionRuns(true);
      const offset = (page - 1) * pageSize;
      try {
        const result = await getDeploymentPredictionRuns(
          deployment.deployment_id,
          pageSize,
          offset,
          sortBy,
          sortDirection,
        );
        if (
          selectedDeploymentRef.current?.deployment_id === deploymentIdAtStart
        ) {
          const { items, total } = result;
          // FR6: fallback to last valid page if current page is now beyond total
          if (items.length === 0 && total > 0 && page > 1) {
            const lastPage = Math.ceil(total / pageSize);
            if (lastPage !== page) {
              setDeploymentRunPage(lastPage);
              fetchDeploymentPredictionRuns(
                deployment,
                lastPage,
                pageSize,
                sortBy,
                sortDirection,
              );
              return;
            }
          }
          setDeploymentPredictionRuns(Array.isArray(items) ? items : []);
          setDeploymentRunTotal(total ?? 0);
          setDeploymentRunPage(page);
        }
      } catch (error) {
        if (
          selectedDeploymentRef.current?.deployment_id === deploymentIdAtStart
        ) {
          setDeploymentPredictionRuns([]);
          setDeploymentRunTotal(0);
        }
      } finally {
        if (
          selectedDeploymentRef.current?.deployment_id === deploymentIdAtStart
        ) {
          setLoadingDeploymentPredictionRuns(false);
        }
      }
    }
  };

  const fetchProjectPredictionRuns = async (
    project: ProjectResponseDto,
    page: number,
    pageSize: number,
    sortBy = "created_at",
    sortDirection: "asc" | "desc" = "desc",
  ) => {
    if (project?.project_id) {
      const projectIdAtStart = project.project_id;
      setLoadingProjectPredictionRuns(true);
      const offset = (page - 1) * pageSize;
      try {
        const result = await getProjectPredictionRuns(
          project.project_id,
          pageSize,
          offset,
          sortBy,
          sortDirection,
        );
        if (selectedProjectRef.current?.project_id === projectIdAtStart) {
          const { items, total } = result;
          // FR6: fallback to last valid page if current page is now beyond total
          if (items.length === 0 && total > 0 && page > 1) {
            const lastPage = Math.ceil(total / pageSize);
            if (lastPage !== page) {
              setProjectRunPage(lastPage);
              fetchProjectPredictionRuns(
                project,
                lastPage,
                pageSize,
                sortBy,
                sortDirection,
              );
              return;
            }
          }
          setProjectPredictionRuns(Array.isArray(items) ? items : []);
          setProjectRunTotal(total ?? 0);
          setProjectRunPage(page);
        }
      } catch (error) {
        if (selectedProjectRef.current?.project_id === projectIdAtStart) {
          setProjectPredictionRuns([]);
          setProjectRunTotal(0);
        }
      } finally {
        if (selectedProjectRef.current?.project_id === projectIdAtStart) {
          setLoadingProjectPredictionRuns(false);
        }
      }
    }
  };

  const fetchProjectHasActive = async (project: ProjectResponseDto) => {
    if (!project?.project_id) return;
    const projectIdAtStart = project.project_id;
    try {
      const hasActive = await getProjectHasActivePredictionRun(
        project.project_id,
      );
      if (selectedProjectRef.current?.project_id === projectIdAtStart) {
        setProjectHasActive(hasActive);
      }
    } catch {
      // fail silently — leave existing state rather than resetting on error
    }
  };

  const fetchProjectImportOrUpload = async (project: ProjectResponseDto) => {
    if (!project?.project_id) return;
    const projectIdAtStart = project.project_id;
    try {
      const hasActive = await getProjectHasActiveImportOrUpload(
        project.project_id,
      );
      if (selectedProjectRef.current?.project_id === projectIdAtStart) {
        setProjectImportOrUploadInProgress(hasActive);
      }
    } catch {
      // fail silently
    }
  };

  const fetchDeploymentHasActive = async (
    deployment: DeploymentResponseDto,
  ) => {
    if (!deployment?.deployment_id) return;
    const deploymentIdAtStart = deployment.deployment_id;
    try {
      const hasActive = await getDeploymentHasActivePredictionRun(
        deployment.deployment_id,
      );
      if (
        selectedDeploymentRef.current?.deployment_id === deploymentIdAtStart
      ) {
        setDeploymentHasActive(hasActive);
      }
    } catch {
      // fail silently
    }
  };

  const fetchDeploymentImportOrUpload = async (
    deployment: DeploymentResponseDto,
  ) => {
    if (!deployment?.deployment_id) return;
    const deploymentIdAtStart = deployment.deployment_id;
    try {
      const hasActive = await getDeploymentHasActiveImportOrUpload(
        deployment.deployment_id,
      );
      if (
        selectedDeploymentRef.current?.deployment_id === deploymentIdAtStart
      ) {
        setImportOrUploadInProgress(hasActive);
      }
    } catch {
      // fail silently
    }
  };

  const handleSelectProject = async (
    project: ProjectResponseDto | null,
    redirect: boolean,
    removeLastSelectedDeployment: boolean = true,
  ) => {
    setSelectedProject(project);
    selectedProjectRef.current = project;
    setSelectedDeployment(null);
    setFilesInAkili(0);
    setDeployments([]);
    setDeploymentPredictionRuns([]);
    setDeploymentRunPage(1);
    setDeploymentRunTotal(0);
    setProjectPredictionRuns([]);
    setProjectRunPage(1);
    setProjectRunTotal(0);
    setProjectHasActive(false);
    setProjectImportOrUploadInProgress(false);
    localStorage.setItem(
      AIMODELZOO_STORAGE_KEYS.PROJECT,
      JSON.stringify(project),
    );
    if (removeLastSelectedDeployment) {
      localStorage.removeItem(AIMODELZOO_STORAGE_KEYS.DEPLOYMENT);
    }
    setSelectedDetectionModel(null);
    setSelectedClassificationModel(null);
    setRetryingRunIds(new Set());
    batchProjectIdRef.current = project?.project_id ?? null;

    if (project === null) {
      navigate("/ai-processing", { replace: true });
      setDeployments([]);
      setDeploymentsLoading(false);
    } else {
      if (redirect) {
        if (
          !window.location.pathname.includes(`/ai-processing/${project.guid}`)
        ) {
          navigate(`/ai-processing/${project.guid}`, { replace: true });
        }
      }
      if (project.project_id && project.project_id > 0) {
        fetchDeployments(project.project_id);
        fetchProjectPredictionRuns(
          project,
          1,
          projectRunPageSize,
          projectRunSortBy,
          projectRunSortDirection,
        );
        fetchProjectHasActive(project);
        fetchProjectImportOrUpload(project);
      }
    }
  };

  const handleSelectDeployment = async (
    deployment: DeploymentResponseDto | null,
    redirect: boolean,
  ) => {
    setDeploymentPredictionRuns([]);
    setDeploymentRunPage(1);
    setDeploymentRunTotal(0);
    setDeploymentHasActive(false);
    setImportOrUploadInProgress(false);
    localStorage.setItem(
      AIMODELZOO_STORAGE_KEYS.DEPLOYMENT,
      JSON.stringify(deployment),
    );
    if (!deployment) {
      navigate(`/ai-processing/${selectedProject?.guid}`, { replace: true });
      setSelectedDeployment(null);
      setFilesInAkili(0);
      const project = selectedProjectRef.current;
      if (project) {
        setProjectRunPage(1);
        fetchProjectHasActive(project);
        fetchProjectImportOrUpload(project);
        fetchProjectPredictionRuns(
          project,
          1,
          projectRunPageSize,
          projectRunSortBy,
          projectRunSortDirection,
        );
      }
      return;
    }
    if (deployment && selectedProject) {
      if (redirect) {
        if (
          !window.location.pathname.includes(
            `/ai-processing/${selectedProject.guid}/${deployment.guid}`,
          )
        ) {
          navigate(
            `/ai-processing/${selectedProject.guid}/${deployment.guid}`,
            { replace: true },
          );
        }
      }
      setSelectedDeployment(deployment);
      fetchFilesInAkili(deployment);
      refreshDeploymentData(deployment, true);
    }
  };

  // ── Confidence threshold change handlers ──────────────────────────────────

  /**
   * Builds an onChange handler that edits the confidence threshold on the
   * given stage's selected model, stripping leading zeros.
   *
   * No-ops while no model is selected: spreading `null` would produce a
   * `{ default_confidence_threshold }` object with no `model_id`, which reads
   * as truthy to `detectionRunDisabled`/`classificationRunDisabled` and let a
   * run be submitted with an undefined `model_id`. The input is also disabled
   * in that state, so this guard is defence in depth.
   */
  const makeConfThreshHandler =
    (setter: React.Dispatch<React.SetStateAction<ModelResponseDto | null>>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value;
      if (value !== "" && !/^0\./.test(value))
        value = value.replace(/^0+/, "") || "0";
      const { name } = e.target;
      setter((prev: any) => (prev ? { ...prev, [name]: value } : prev));
    };

  const handleDetectionConfThreshChange = makeConfThreshHandler(
    setSelectedDetectionModel,
  );
  const handleClassificationConfThreshChange = makeConfThreshHandler(
    setSelectedClassificationModel,
  );

  // ── Model selection helpers ───────────────────────────────────────────────

  const applyDefaultThresh = (model: ModelResponseDto): ModelResponseDto => ({
    ...model,
    default_confidence_threshold:
      model.default_confidence_threshold !== undefined
        ? (Number(model.default_confidence_threshold) * 100).toString()
        : "",
  });

  // AKL-766: selecting a model for one stage must not clear the other stage's
  // selection — "Run full pipeline" needs both a detection AND a
  // classification model chosen at once.
  const handleSelectDetectionModel = (model: ModelResponseDto | null) =>
    setSelectedDetectionModel(model ? applyDefaultThresh(model) : null);

  const handleSelectClassificationModel = (model: ModelResponseDto | null) =>
    setSelectedClassificationModel(model ? applyDefaultThresh(model) : null);

  // ── Validation ────────────────────────────────────────────────────────────

  const getConfThreshError = (value: string | undefined): string => {
    if (!value || value === "") return "";
    const num = Number(value);
    if (isNaN(num) || num < 0 || num > 100)
      return t("Must be between 0 and 100");
    return "";
  };

  const detectionConfThreshError = getConfThreshError(
    selectedDetectionModel?.default_confidence_threshold,
  );
  const classificationConfThreshError = getConfThreshError(
    selectedClassificationModel?.default_confidence_threshold,
  );

  // ── Inference (detection / classification / full pipeline) ───────────────

  const resolveConfThresh = (
    selectedModel: ModelResponseDto | null,
    originalModels: ModelResponseDto[],
  ): number | undefined => {
    if (!selectedModel) return undefined;
    const original = originalModels.find(
      (m) => m.model_id === selectedModel.model_id,
    );
    const raw = selectedModel.default_confidence_threshold;
    const thresh =
      raw === ""
        ? Number(original?.default_confidence_threshold)
        : Number(raw) / 100;
    return isNaN(thresh) ? undefined : thresh;
  };

  const TOAST_DETAIL_LIMIT = 5;

  const formatDeploymentList = (rows: BatchInferenceResult[]): string =>
    rows
      .map((r) =>
        r.error_message
          ? `${r.deployment_name}: ${r.error_message}`
          : r.deployment_name,
      )
      .join("\n");

  const toastSkippedAndFailed = (
    results: BatchInferenceResult[],
    failedFallbackKey: string,
    skippedFallbackKey: string,
  ) => {
    const skipped = results.filter((r) => r.status === "skipped");
    const failed = results.filter((r) => r.status === "failed");
    if (skipped.length > 0) {
      const body =
        skipped.length <= TOAST_DETAIL_LIMIT
          ? `${t("Skipped deployments:")}\n${formatDeploymentList(skipped)}`
          : t(skippedFallbackKey, { n: skipped.length });
      toast.info(body, { style: { whiteSpace: "pre-line" } });
    }
    if (failed.length > 0) {
      const body =
        failed.length <= TOAST_DETAIL_LIMIT
          ? `${t("Failed deployments:")}\n${formatDeploymentList(failed)}`
          : t(failedFallbackKey, { n: failed.length });
      toast.error(body, { style: { whiteSpace: "pre-line" } });
    }
  };

  const refreshAfterQueue = () => {
    const project = selectedProjectRef.current;
    const deployment = selectedDeploymentRef.current;
    if (isAllDeployments && project) {
      fetchProjectHasActive(project);
      setProjectRunPage(1);
      fetchProjectPredictionRuns(
        project,
        1,
        projectRunPageSize,
        projectRunSortBy,
        projectRunSortDirection,
      );
    } else if (deployment) {
      refreshDeploymentData(deployment, true);
    }
  };

  /**
   * Queues detection — across the whole project ("All deployments" scope) or
   * on the single selected deployment. When pipelineClassification is
   * provided (AKL-766 "Run full pipeline"), the classification half is
   * queued alongside it server-side (PENDING until detection completes).
   */
  const handleDetectDeployment = async (pipelineClassification?: {
    model: ModelResponseDto;
    conf_thresh: number | undefined;
  }) => {
    if (!selectedDetectionModel) return;
    const confThresh = resolveConfThresh(
      selectedDetectionModel,
      detectionModels,
    );
    if (isAllDeployments) {
      const project = selectedProjectRef.current;
      if (!project) return;
      const ids = deployments.map((d) => d.deployment_id);
      if (ids.length === 0) {
        toast.error(t("No deployments in this project."));
        return;
      }
      const projectIdAtStart = batchProjectIdRef.current;
      setDetecting(true);
      try {
        const data: BatchInferenceDto = {
          deployment_ids: ids,
          model_id: selectedDetectionModel.model_id,
          conf_thresh: confThresh,
          ...(pipelineClassification
            ? {
                pipeline_classification_model_id:
                  pipelineClassification.model.model_id,
                pipeline_classification_conf_thresh:
                  pipelineClassification.conf_thresh,
              }
            : {}),
        };
        const results = await batchDetectDeployments(data);
        if (batchProjectIdRef.current !== projectIdAtStart) return;
        toast.success(
          pipelineClassification
            ? t(
                "Pipeline queued for {{accepted}} of {{total}} deployments — they'll start within a minute.",
                {
                  accepted: results.summary.accepted,
                  total: results.summary.total,
                },
              )
            : t(
                "Queued detection for {{accepted}} of {{total}} deployments — they'll start within a minute.",
                {
                  accepted: results.summary.accepted,
                  total: results.summary.total,
                },
              ),
        );
        toastSkippedAndFailed(
          results.results,
          "{{n}} deployments failed.",
          "{{n}} deployments skipped.",
        );
        refreshAfterQueue();
      } catch (err) {
        console.error("Error running batch detection:", err);
        toast.error(t("Batch detection failed."));
      } finally {
        setDetecting(false);
      }
      return;
    }

    if (!selectedDeployment) return;
    const deploymentAtStart = selectedDeployment;
    setDetecting(true);
    try {
      const inferenceData: DeploymentInferenceDto = {
        deployment_id: deploymentAtStart.deployment_id,
        model_id: selectedDetectionModel.model_id,
        conf_thresh: confThresh,
        ...(pipelineClassification
          ? {
              pipeline_classification_model_id:
                pipelineClassification.model.model_id,
              pipeline_classification_conf_thresh:
                pipelineClassification.conf_thresh,
            }
          : {}),
      };
      await detectDeployment(inferenceData);
      if (
        selectedDeploymentRef.current?.deployment_id ===
        deploymentAtStart.deployment_id
      ) {
        await refreshDeploymentData(deploymentAtStart, true);
      }
      toast.success(
        pipelineClassification ? t("Pipeline started") : t("Detection started"),
      );
    } catch (error: any) {
      console.error("Error starting detection:", error);
      if (error?.status) {
        toast.error(error.message);
        if (error.status === 409) {
          fetchDeploymentHasActive(deploymentAtStart);
        }
      }
    } finally {
      setDetecting(false);
    }
  };

  const handleClassifyDeployment = async () => {
    if (!selectedClassificationModel) return;
    const confThresh = resolveConfThresh(
      selectedClassificationModel,
      classifierModels,
    );

    if (isAllDeployments) {
      const project = selectedProjectRef.current;
      if (!project) return;
      const ids = deployments.map((d) => d.deployment_id);
      if (ids.length === 0) {
        toast.error(t("No deployments in this project."));
        return;
      }
      const projectIdAtStart = batchProjectIdRef.current;
      setClassifying(true);
      try {
        const data: BatchInferenceDto = {
          deployment_ids: ids,
          model_id: selectedClassificationModel.model_id,
          conf_thresh: confThresh,
        };
        const results = await batchClassifyDeployments(data);
        if (batchProjectIdRef.current !== projectIdAtStart) return;
        toast.success(
          t(
            "Queued classification for {{accepted}} of {{total}} deployments — they'll start within a minute.",
            {
              accepted: results.summary.accepted,
              total: results.summary.total,
            },
          ),
        );
        toastSkippedAndFailed(
          results.results,
          "{{n}} deployments failed.",
          "{{n}} deployments skipped.",
        );
        refreshAfterQueue();
      } catch (err) {
        console.error("Error running batch classification:", err);
        toast.error(t("Batch classification failed."));
      } finally {
        setClassifying(false);
      }
      return;
    }

    if (!selectedDeployment) return;
    const deploymentAtStart = selectedDeployment;
    setClassifying(true);
    try {
      const inferenceData: DeploymentInferenceDto = {
        deployment_id: deploymentAtStart.deployment_id,
        model_id: selectedClassificationModel.model_id,
        conf_thresh: confThresh,
      };
      await classifyDeployment(inferenceData);
      if (
        selectedDeploymentRef.current?.deployment_id ===
        deploymentAtStart.deployment_id
      ) {
        await refreshDeploymentData(deploymentAtStart, true);
      }
      toast.success(t("Classification started"));
    } catch (error: any) {
      console.error("Error starting classification:", error);
      if (error?.status) {
        toast.error(error.message);
        if (error.status === 409) {
          fetchDeploymentHasActive(deploymentAtStart);
        }
      }
    } finally {
      setClassifying(false);
    }
  };

  const handleRunFullPipeline = async () => {
    if (!selectedClassificationModel) return;
    await handleDetectDeployment({
      model: selectedClassificationModel,
      conf_thresh: resolveConfThresh(
        selectedClassificationModel,
        classifierModels,
      ),
    });
  };

  const handleRetrySingleFailedRun = async (run: PredictionRunItem) => {
    if (!run.deployment_id || !run.id) return;
    setRetryingRunIds((prev) => {
      const next = new Set(prev);
      next.add(run.id);
      return next;
    });
    try {
      await retryPredictionRun(run.deployment_id, run.id);
      toast.success(t("Run retried"));
      const project = selectedProjectRef.current;
      if (project) {
        setProjectRunPage(1);
        setProjectRunSortBy("created_at");
        setProjectRunSortDirection("desc");
        fetchProjectHasActive(project);
        fetchProjectPredictionRuns(
          project,
          1,
          projectRunPageSize,
          "created_at",
          "desc",
        );
      }
      const deployment = selectedDeploymentRef.current;
      if (deployment) {
        setDeploymentRunPage(1);
        setDeploymentRunSortBy("created_at");
        setDeploymentRunSortDirection("desc");
        fetchDeploymentPredictionRuns(
          deployment,
          1,
          deploymentRunPageSize,
          "created_at",
          "desc",
        );
      }
    } catch (error: any) {
      console.error("Error retrying failed run:", error);
      if (error?.status) {
        toast.error(error.message);
      }
    } finally {
      setRetryingRunIds((prev) => {
        const next = new Set(prev);
        next.delete(run.id);
        return next;
      });
    }
  };

  // ── Refresh all data for a deployment ────────────────────────────────────

  const refreshDeploymentData = async (
    deployment: DeploymentResponseDto,
    resetPage = false,
  ) => {
    if (deployment) {
      const page = resetPage ? 1 : deploymentRunPage;
      if (resetPage) setDeploymentRunPage(1);
      await Promise.all([
        fetchDeploymentPredictionRuns(
          deployment,
          page,
          deploymentRunPageSize,
          deploymentRunSortBy,
          deploymentRunSortDirection,
        ),
        fetchDeploymentHasActive(deployment),
        fetchDeploymentImportOrUpload(deployment),
      ]);
    }
  };

  // Keep ref in sync so async handlers can read the current deployment value
  // without stale-closure issues.
  useEffect(() => {
    selectedDeploymentRef.current = selectedDeployment;
  }, [selectedDeployment]);

  // ── Derive in-progress status from deploymentHasActive/projectHasActive ──

  useEffect(() => {
    setInferenceInProgress(
      detecting ||
        classifying ||
        (isAllDeployments ? projectHasActive : deploymentHasActive),
    );
  }, [
    detecting,
    classifying,
    isAllDeployments,
    projectHasActive,
    deploymentHasActive,
  ]);

  return (
    <AiModelZooDetails
      projects={projects}
      detectionModels={detectionModels}
      classifierModels={classifierModels}
      selectedDetectionModel={selectedDetectionModel}
      onSelectDetectionModel={handleSelectDetectionModel}
      selectedProject={selectedProject}
      loading={loadingProjects}
      deploymentsLoading={deploymentsLoading}
      modelsLoading={modelsLoading}
      resultsLoaded={resultsLoaded}
      deployments={deployments}
      onSelectProject={handleSelectProject}
      onSelectDeployment={handleSelectDeployment}
      selectedDeployment={selectedDeployment}
      isAllDeployments={isAllDeployments}
      onSelectClassificationModel={handleSelectClassificationModel}
      selectedClassificationModel={selectedClassificationModel}
      filesInAkili={filesInAkili}
      loadingFilesInAkili={loadingFilesInAkili}
      classifying={classifying}
      detecting={detecting}
      predictionRuns={deploymentPredictionRuns}
      loadingPredictionRuns={loadingDeploymentPredictionRuns}
      projectPredictionRuns={projectPredictionRuns}
      loadingProjectPredictionRuns={loadingProjectPredictionRuns}
      onRefreshDeploymentRuns={() => {
        if (selectedDeployment) refreshDeploymentData(selectedDeployment);
      }}
      onRefreshProjectRuns={() => {
        if (selectedProject) {
          setProjectRunPage(1);
          fetchProjectPredictionRuns(
            selectedProject,
            1,
            projectRunPageSize,
            projectRunSortBy,
            projectRunSortDirection,
          );
          fetchProjectHasActive(selectedProject);
          fetchProjectImportOrUpload(selectedProject);
        }
      }}
      projectRunPagination={{
        page: projectRunPage,
        pageSize: projectRunPageSize,
        total: projectRunTotal,
        onPageChange: (page) => {
          setProjectRunPage(page);
          if (selectedProject)
            fetchProjectPredictionRuns(
              selectedProject,
              page,
              projectRunPageSize,
              projectRunSortBy,
              projectRunSortDirection,
            );
        },
        onPageSizeChange: (size) => {
          setProjectRunPageSize(size);
          setProjectRunPage(1);
          if (selectedProject)
            fetchProjectPredictionRuns(
              selectedProject,
              1,
              size,
              projectRunSortBy,
              projectRunSortDirection,
            );
        },
      }}
      projectRunSort={{
        sortBy: projectRunSortBy,
        sortDirection: projectRunSortDirection,
        onSort: (column) => {
          const newDirection =
            projectRunSortBy === column && projectRunSortDirection === "desc"
              ? "asc"
              : "desc";
          setProjectRunSortBy(column);
          setProjectRunSortDirection(newDirection);
          setProjectRunPage(1);
          if (selectedProject)
            fetchProjectPredictionRuns(
              selectedProject,
              1,
              projectRunPageSize,
              column,
              newDirection,
            );
        },
      }}
      deploymentRunPagination={{
        page: deploymentRunPage,
        pageSize: deploymentRunPageSize,
        total: deploymentRunTotal,
        onPageChange: (page) => {
          setDeploymentRunPage(page);
          if (selectedDeployment)
            fetchDeploymentPredictionRuns(
              selectedDeployment,
              page,
              deploymentRunPageSize,
              deploymentRunSortBy,
              deploymentRunSortDirection,
            );
        },
        onPageSizeChange: (size) => {
          setDeploymentRunPageSize(size);
          setDeploymentRunPage(1);
          if (selectedDeployment)
            fetchDeploymentPredictionRuns(
              selectedDeployment,
              1,
              size,
              deploymentRunSortBy,
              deploymentRunSortDirection,
            );
        },
      }}
      deploymentRunSort={{
        sortBy: deploymentRunSortBy,
        sortDirection: deploymentRunSortDirection,
        onSort: (column) => {
          const newDirection =
            deploymentRunSortBy === column &&
            deploymentRunSortDirection === "desc"
              ? "asc"
              : "desc";
          setDeploymentRunSortBy(column);
          setDeploymentRunSortDirection(newDirection);
          setDeploymentRunPage(1);
          if (selectedDeployment)
            fetchDeploymentPredictionRuns(
              selectedDeployment,
              1,
              deploymentRunPageSize,
              column,
              newDirection,
            );
        },
      }}
      onDetectionConfThreshChange={handleDetectionConfThreshChange}
      onClassificationConfThreshChange={handleClassificationConfThreshChange}
      importOrUploadInProgress={
        isAllDeployments
          ? projectImportOrUploadInProgress
          : importOrUploadInProgress
      }
      inferenceInProgress={inferenceInProgress}
      onRetryFailedRun={handleRetrySingleFailedRun}
      retryingRunIds={retryingRunIds}
      detectionConfThreshError={detectionConfThreshError}
      classificationConfThreshError={classificationConfThreshError}
      confirmAction={confirmAction}
      onOpenConfirm={setConfirmAction}
      onCloseConfirm={() => setConfirmAction(null)}
      onConfirmRun={() => {
        const action = confirmAction;
        setConfirmAction(null);
        if (action === "detection") handleDetectDeployment();
        else if (action === "classification") handleClassifyDeployment();
        else if (action === "pipeline") handleRunFullPipeline();
      }}
    />
  );
};

export default ManageAiModelZooPage;
